import React, { Children, cloneElement, isValidElement, useEffect, useState } from 'react';
import useReveal from '../hooks/useReveal';

const detectMobile = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(max-width: 767px)').matches || /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || ''));

/**
 * Reveal — premium banking-grade reveal-on-scroll.
 *
 * Desktop : 1000ms opacity + 28px translate + light blur dissolve.
 * Mobile  : NO scroll reveal at all — content is visible immediately. Real
 *           banking apps (N26, Revolut, Trade Republic) keep scrolling
 *           deterministic. Animating while the user scrolls always feels
 *           janky on mobile, no matter how it's tuned.
 */
const Reveal = ({ children, delay = 0, distance, as: Tag = 'div', className = '', ...rest }) => {
  const [mobile, setMobile] = useState(true); // assume mobile until measured (SSR-safe)

  useEffect(() => {
    setMobile(detectMobile());
    const mq = window.matchMedia?.('(max-width: 767px)');
    if (!mq) return;
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Mobile: render plainly, no observer, no animation.
  if (mobile) {
    return <Tag className={className} {...rest}>{children}</Tag>;
  }

  return <DesktopReveal delay={delay} distance={distance} as={Tag} className={className} {...rest}>{children}</DesktopReveal>;
};

const DesktopReveal = ({ children, delay = 0, distance = 28, as: Tag = 'div', className = '', ...rest }) => {
  const [ref, visible] = useReveal();
  const dur = 1050;
  const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
  return (
    <Tag
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate3d(0,0,0)' : `translate3d(0,${distance}px,0)`,
        filter: visible ? 'blur(0)' : 'blur(4px)',
        transition: `opacity ${dur}ms ${easing} ${delay}ms, transform ${dur + 100}ms ${easing} ${delay}ms, filter 850ms ${easing} ${delay}ms`,
        willChange: 'opacity, transform',
        backfaceVisibility: 'hidden',
      }}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * RevealStagger — wraps direct children, each child reveals with a cascading delay.
 * On mobile this is a no-op (just renders children plainly).
 */
export const RevealStagger = ({ children, step = 140, baseDelay = 0, distance, as: Tag = 'div', className = '', ...rest }) => {
  const arr = Children.toArray(children);
  return (
    <Tag className={className} {...rest}>
      {arr.map((child, i) => {
        const d = baseDelay + i * step;
        if (!isValidElement(child)) {
          return <Reveal key={i} delay={d} distance={distance}>{child}</Reveal>;
        }
        return (
          <Reveal key={child.key ?? i} delay={d} distance={distance}>
            {cloneElement(child)}
          </Reveal>
        );
      })}
    </Tag>
  );
};

export default Reveal;
